import { Button, DropdownSelect, Modal } from '@components/common';
import { FormInput, FormTextarea } from '@components/common/FormField';
import React, { useState } from 'react';
import {
  INVENTORY_STOCK_LOCATION_TYPE_OPTIONS,
  InventoryStockLocationFormState,
} from './stockLocationForm';

interface StockLocationEditorModalProps {
  initial: InventoryStockLocationFormState;
  isSaving: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (form: InventoryStockLocationFormState) => void;
}

export function StockLocationEditorModal({
  initial,
  isSaving,
  title,
  onClose,
  onSubmit,
}: StockLocationEditorModalProps) {
  const [form, setForm] = useState<InventoryStockLocationFormState>(initial);

  const set = <K extends keyof InventoryStockLocationFormState>(
    key: K,
    value: InventoryStockLocationFormState[K],
  ) => setForm((current) => ({ ...current, [key]: value }));

  return (
    <Modal isOpen onClose={onClose} title={title} size="lg">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
        className="space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormInput
            label="Location name"
            required
            value={form.name}
            onChange={(event) => set('name', event.target.value)}
            placeholder="e.g. Main Store"
          />
          <DropdownSelect
            label="Location type"
            value={form.type}
            options={INVENTORY_STOCK_LOCATION_TYPE_OPTIONS}
            onChange={(nextValue) =>
              set('type', nextValue as InventoryStockLocationFormState['type'])
            }
          />
        </div>

        <FormTextarea
          label="Description"
          value={form.description}
          onChange={(event) => set('description', event.target.value)}
          placeholder="Optional note about what is stored here"
          rows={3}
        />

        <label className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-3 text-sm text-gray-700">
          <input
            type="checkbox"
            aria-label="Location is active"
            checked={form.isActive}
            onChange={(event) => set('isActive', event.target.checked)}
          />
          Location is active
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSaving}>
            Save location
          </Button>
        </div>
      </form>
    </Modal>
  );
}
